import { useEffect, useState } from "react";
import { listPrincipals, savePrincipal, type Capability, type Principal } from "./actions";

// Users and Access (admin only). Two classes: admin (everything) and staff (exactly the boxes ticked here). A person needs a record here AND a
// sign-in in Firebase Authentication (public sign-up is disabled on purpose, so an admin creates the sign-in there). Nobody can demote or disable
// themselves, and there is always at least one active admin.

export function AccessPanel({ myEmail }: { myEmail: string }) {
  const [rows, setRows] = useState<Principal[]>([]);
  const [caps, setCaps] = useState<Capability[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [draft, setDraft] = useState<Principal | null>(null);
  const [email, setEmail] = useState("");

  const load = async () => {
    const r = await listPrincipals();
    if (!r.ok) { setError(r.error); return; }
    setError(null); setRows(r.data.rows); setCaps(r.data.capabilities);
  };
  useEffect(() => { load(); }, []);

  const groups = Array.from(new Set(caps.map((c) => c.group)));
  const edit = (p: Principal) => { setDraft({ ...p, caps: [...p.caps] }); setMsg(null); };
  const toggle = (k: string) => draft && setDraft({ ...draft, caps: draft.caps.includes(k) ? draft.caps.filter((x) => x !== k) : [...draft.caps, k] });
  const save = async () => {
    if (!draft) return;
    const r = await savePrincipal({ email: draft.email, class: draft.class, caps: draft.caps, disabled: draft.disabled, note: draft.note });
    if (!r.ok) { setError(r.error); return; }
    setError(null); setMsg(`Saved ${draft.email}.`); setDraft(null); await load();
  };

  return (
    <div>
      <h1>Users and access</h1>
      <p className="meta">
        Admin sees and changes everything. Staff see and change only what is ticked. A new person also needs a sign-in created in Firebase Authentication
        (sign-up is disabled on purpose). Changes apply on their next page load.
      </p>
      {error && <div className="panel error">{error}</div>}
      {msg && <div className="panel">{msg}</div>}
      <table className="manual-products">
        <thead><tr><th>Person</th><th>Class</th><th>Can</th><th>Status</th><th>Last changed</th><th></th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.email}>
              <td>{r.email}{r.email === myEmail ? " (you)" : ""}</td><td>{r.class}</td>
              <td className="wrap" style={{ whiteSpace: "normal", maxWidth: 520 }}>{r.class === "admin" ? "Everything" : r.caps.length ? r.caps.map((k) => caps.find((c) => c.key === k)?.label ?? k).join(", ") : "Nothing yet"}</td>
              <td>{r.disabled ? "Disabled" : "Active"}</td><td className="mono">{r.updated_at.slice(0, 10)} {r.updated_by}</td>
              <td><button type="button" onClick={() => edit(r)}>Edit</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="panel" style={{ marginTop: 16, maxWidth: 760 }}>
        <h3 style={{ marginTop: 0 }}>Add a person</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input placeholder="email address" value={email} onChange={(e) => setEmail(e.target.value)} style={{ minWidth: 260 }} />
          <button type="button" disabled={!email.includes("@")} onClick={() => { edit({ email: email.trim().toLowerCase(), class: "staff", caps: ["view.dashboard", "view.sheet"], disabled: false, updated_by: "", updated_at: "", note: "" }); setEmail(""); }}>Set up access</button>
        </div>
      </div>

      {draft && (
        <div className="panel" style={{ marginTop: 16, maxWidth: 760 }}>
          <h3 style={{ marginTop: 0 }}>{draft.email}</h3>
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <label>Class{" "}
              <select value={draft.class} onChange={(e) => setDraft({ ...draft, class: e.target.value as "admin" | "staff" })}>
                <option value="staff">Staff</option><option value="admin">Admin (everything)</option>
              </select>
            </label>
            <label><input type="checkbox" checked={draft.disabled} onChange={(e) => setDraft({ ...draft, disabled: e.target.checked })} /> Disabled (cannot use the console)</label>
          </div>
          {draft.class === "staff" && groups.map((g) => (
            <fieldset key={g} style={{ marginTop: 12, border: "1px solid var(--line)", borderRadius: 6 }}>
              <legend>{g}</legend>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 6 }}>
                {caps.filter((c) => c.group === g).map((c) => (
                  <label key={c.key}><input type="checkbox" checked={draft.caps.includes(c.key)} onChange={() => toggle(c.key)} /> {c.label}</label>
                ))}
              </div>
            </fieldset>
          ))}
          <input placeholder="Note (optional)" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} style={{ marginTop: 12, width: "100%" }} />
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button type="button" onClick={save}>Save access</button><button type="button" onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
